import { BREAKPOINTS } from '../constants.js';

export const resolveNode = (node, activeBreakpoint = 'default') => {
    if (!node) return node;

    // If default, just return node as is (but we still need to resolve attached components if they have overrides?)
    // Actually no, if breakpoint is default, we want base values.
    // Base values are stored directly on the object.
    // BUT, we should clone to be safe if we are mutating in the loop below.

    // Optimization: If no overrides exist anywhere, return node.
    // But we have to check attachedComponents too.

    const hasNodeOverrides = node.props?.responsiveOverrides;
    const hasCompOverrides = node.attachedComponents?.some(c => c.props?.responsiveOverrides);

    if (activeBreakpoint === 'default' || (!hasNodeOverrides && !hasCompOverrides)) {
        return node;
    }

    const resolved = { ...node, props: { ...node.props } };
    const overrides = node.props.responsiveOverrides || {};

    const sortedBps = Object.keys(BREAKPOINTS).sort((a,b) => BREAKPOINTS[a] - BREAKPOINTS[b]);
    const activeWidth = BREAKPOINTS[activeBreakpoint];

    for (const bp of sortedBps) {
        if (bp === 'default') continue;
        if (BREAKPOINTS[bp] > activeWidth) break;

        const bpOverrides = overrides[bp];
        if (bpOverrides) {
            // 1. Merge Props (excluding responsiveOverrides itself to prevent recursion/mess)
            const { layout_x, layout_y, layout_width, layout_height, layout_anchors, className, ...restProps } = bpOverrides;

            // Handle className specially - it should be ADDITIVE (append breakpoint-prefixed classes)
            // not REPLACEMENT, because variant-data needs all breakpoints in the class string
            if (className) {
                // Add breakpoint prefix to each class if not already present
                // This ensures variant-data classes have the correct breakpoint context
                const prefixedClasses = className.split(' ')
                    .filter(Boolean)
                    .map(cls => {
                        // If class already has a breakpoint prefix, don't add another
                        const hasPrefix = Object.keys(BREAKPOINTS).some(b =>
                            b !== 'default' && cls.startsWith(`${b}:`)
                        );
                        return hasPrefix ? cls : `${bp}:${cls}`;
                    })
                    .join(' ');

                // Append the breakpoint-prefixed className to the existing className
                const existingClassName = resolved.props.className || '';
                resolved.props.className = [existingClassName, prefixedClasses].filter(Boolean).join(' ');
            }

            Object.assign(resolved.props, restProps);

            // 2. Merge Root Transform
            if (layout_x !== undefined) resolved.x = layout_x;
            if (layout_y !== undefined) resolved.y = layout_y;
            if (layout_width !== undefined) resolved.width = layout_width;
            if (layout_height !== undefined) resolved.height = layout_height;
            if (layout_anchors !== undefined) resolved.anchors = layout_anchors;
        }
    }

    // 3. Resolve Attached Components
    if (resolved.attachedComponents) {
        resolved.attachedComponents = resolved.attachedComponents.map(comp => {
            const compOverrides = comp.props?.responsiveOverrides;
            if (!compOverrides) return comp;

            const resolvedComp = { ...comp, props: { ...comp.props } };

            for (const bp of sortedBps) {
                if (bp === 'default') continue;
                if (BREAKPOINTS[bp] > activeWidth) break;

                if (compOverrides[bp]) {
                    Object.assign(resolvedComp.props, compOverrides[bp]);
                }
            }
            return resolvedComp;
        });
    }

    return resolved;
};
